import { map } from 'rxjs/operators';
import { Injectable, OnInit } from '@angular/core';
import { Observable, of } from 'rxjs';
import { Inspection, InspectionPlugin } from '../models/index';
import { ApiService } from './api.service';
import { DataService } from './data.service';
import { BaseService } from './baseService';

@Injectable({
  providedIn: 'root'
})
export class BaseInspectionService extends BaseService {
  protected loader: Observable<Inspection[]>;

  constructor(protected apiService: ApiService, protected dataService: DataService) {
    super();
  }

  addSection(id: string, sourceInspectionId: string, sourceSectionId: string[]): Observable<Inspection> {
    return null;
  }
  addPoints(
    id: string,
    targetSectionId: string,
    sourceInspectionId: string,
    sourcePointsId: string[],
    targetFolderId: string
  ): Observable<Inspection> {
    return null;
  }

  public exportMyBobstParts(id: string): any {
    const url = `api/inspections/${id}/plugins/parts/mybobst/export`;
    window.open(url, '_blank');
  }

  public exportMyBobstCsvParts(id: string): any {
    const url = `api/inspections/${id}/plugins/parts/mybobst/export/csv`;
    window.open(url, '_blank');
  }

  public downloadQuotationFile(id: string, quotationNumber: string): any {
    const url = `api/inspections/${id}/plugins/parts/quotation/${quotationNumber}/download`;
    window.open(url, '_blank');
  }

  public uploadParts(id: string, files: any): Observable<Inspection> {
    const url = '/inspections/' + id + '/plugins/parts/upload/';
    return this.apiService.postFormData(url, files).pipe(
      map((plugin: InspectionPlugin) => {
        return this.dataService.updatePlugin(id, plugin);
      })
    );
  }

  public uploadPartsPrices(id: string, files: any): Observable<Inspection> {
    const url = '/inspections/' + id + '/plugins/parts/prices/import';
    return this.apiService.postFormData(url, files).pipe(
      map((plugin: InspectionPlugin) => {
        return this.dataService.updatePlugin(id, plugin);
      })
    );
  }

  public uploadQuotation(id: string, files: any) {
    const url = '/inspections/' + id + '/plugins/parts/quotation/import';
    return this.apiService.postFormData(url, files).pipe(
      map((plugin: InspectionPlugin) => {
        this.dataService.updatePlugin(id, plugin);
        return plugin;
      })
    );
  }

  public synchronizeQuotation(id: string, quotationNumber: string) {
    const url = `/inspections/${id}/plugins/parts/quotation/${quotationNumber}/synchronize`;
    return this.apiService.post(url).pipe(
      map((plugin: InspectionPlugin) => {
        this.dataService.updatePlugin(id, plugin);
        return plugin;
      })
    );
  }

  public synchronizeSap(id: string, resetToDraft = true) {
    const url = `/inspections/${id}/plugins/parts/sap/synchronize?resetToDraft=${resetToDraft}`;
    return this.apiService.post(url).pipe(
      map((plugin: InspectionPlugin) => {
        this.dataService.updatePlugin(id, plugin, resetToDraft);
        const currency = plugin.config.Parts?.[0]?.Currency;
        if (currency) {
          this.dataService.updateInspectionCurrency(id, currency);
        }
        return plugin;
      })
    );
  }

  public checkSapPrices(id: string): Observable<{ available: boolean }> {
    if(!this.isConnection) return of({ available: false });
    const url = `/inspections/${id}/plugins/parts/sap/checkprices`;
    return this.apiService.get(url);
  }

  public dismissPartStatuses(id: string) {
    const url = `/inspections/${id}/plugins/parts/quotation/dismiss`;
    return this.apiService.post(url).pipe(
      map((plugin: InspectionPlugin) => {
        this.dataService.updatePlugin(id, plugin);
        return plugin;
      })
    );
  }

  public checkQuotation(id: string, quotationNumber: string): Observable<{ exists: boolean; date: Date }> {
    const url = `/inspections/${id}/plugins/parts/quotation/${quotationNumber}/check`;
    return this.isConnection ? this.apiService.get(url) : of({ exists: false, date: null });
  }

  public orderSections(id: string, orderableItems: any) {
    const url = '/inspections/' + id + '/ordersections';
    return this.apiService.post(url, { OrderableItems: orderableItems }).pipe(
      map((inspection: Inspection) => {
        this.dataService.updateInspectionSectionsOrder(id, orderableItems);
      })
    );
  }

  public orderPlugins(id: string, orderableItems: any) {
    const url = '/inspections/' + id + '/orderplugins';
    return this.apiService.post(url, { OrderableItems: orderableItems }).pipe(
      map((inspection: Inspection) => {
        this.dataService.updateInspectionPluginsOrder(id, orderableItems);
      })
    );
  }

  public deleteInspection(id: string): any {
    const url = '/inspections/' + id;
    return this.apiService.delete(url).pipe(
      map((success: boolean) => {
        if (success) {
          this.dataService.removeInspection(id);
        }
        return success;
      })
    );
  }

  public updateInspectionProperties(id: string, params: any): Observable<Inspection> {
    const url = '/inspections/' + id;
    return this.apiService.patch(url, params).pipe(
      map((inspection: Inspection) => {
        inspection.isLoaded = true;
        this.dataService.processInspection(inspection);
        return inspection;
      })
    );
  }
}
